import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';
import type { PhysicalCountSession } from '../../../types/physicalCount.types';
import PhysicalCountDetailGroup from './PhysicalCountDetailGroup';
import { usePhysicalCountDetailModal } from './PhysicalCountDetailModal.hooks';
import { PHYSICAL_COUNT_DETAIL_GROUPS } from './physicalCountDetailGroups';
import { PHYSICAL_COUNT_DETAIL_SHOW_INLINE_GROUPS } from './physicalCountDetail.config';
import { formatDateTime } from '../../../utils/formatters/date';
import {
  PHYSICAL_COUNT_METRICS,
  type PhysicalCountMetricKey,
} from '../physicalCount/physicalCountMetrics.config';
import PhysicalCountMetricInfoButton from '../physicalCount/PhysicalCountMetricInfoButton';

const DETAIL_METRIC_ACCENTS: Record<
  PhysicalCountMetricKey,
  'neutral' | 'success' | 'error' | 'warning' | 'info'
> = {
  expected: 'neutral',
  scanned: 'neutral',
  ok: 'success',
  missing: 'error',
  excess: 'warning',
  duplicate: 'warning',
  notAvailable: 'info',
};

const formatDisplayDateTime = (dateStr?: string) => {
  if (dateStr == null || dateStr === '') return '-';
  return formatDateTime(dateStr) || '-';
};

/** Tarjeta de métrica alineada con tema claro/oscuro */
function SummaryStatCard(props: {
  label: string;
  value: number | string | undefined;
  accent: 'neutral' | 'success' | 'error' | 'warning' | 'info';
  tooltip?: string;
}) {
  const { label, value, accent, tooltip } = props;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const borderColor =
    accent === 'neutral'
      ? theme.palette.divider
      : accent === 'success'
        ? theme.palette.success.main
        : accent === 'error'
          ? theme.palette.error.main
          : accent === 'warning'
            ? theme.palette.warning.main
            : theme.palette.info.main;

  const labelColor =
    accent === 'neutral'
      ? 'text.secondary'
      : accent === 'success'
        ? 'success.main'
        : accent === 'error'
          ? 'error.main'
          : accent === 'warning'
            ? 'warning.main'
            : 'info.main';

  const valueColor = accent === 'neutral' ? 'text.primary' : labelColor;

  const bg =
    accent === 'neutral'
      ? isDark
        ? alpha(theme.palette.common.white, 0.06)
        : theme.palette.background.paper
      : isDark
        ? alpha(borderColor, 0.14)
        : theme.palette.background.paper;

  return (
    <Box
      sx={{
        position: 'relative',
        p: 1.5,
        pb: tooltip != null ? 2.75 : 1.5,
        borderRadius: 1,
        bgcolor: bg,
        border: '1px solid',
        borderColor,
        minWidth: 80,
      }}
    >
      <Typography variant="caption" sx={{ color: labelColor }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ color: valueColor, fontWeight: 600 }}>
        {value ?? '-'}
      </Typography>
      {tooltip != null && <PhysicalCountMetricInfoButton tooltip={tooltip} label={label} />}
    </Box>
  );
}

export interface PhysicalCountDetailModalProps {
  open: boolean;
  onClose: () => void;
  session: PhysicalCountSession | null;
}

const PhysicalCountDetailModal: React.FC<PhysicalCountDetailModalProps> = ({
  open,
  onClose,
  session,
}) => {
  const theme = useTheme();
  const {
    summary,
    summaryLoading,
    summaryError,
    groups,
    toggleGroup,
    changeGroupPage,
    changeGroupItemsPerPage,
    expandAllGroups,
    collapseAllGroups,
    hasExpandableGroups,
  } = usePhysicalCountDetailModal(open, session, {
    enableInlineGroups: PHYSICAL_COUNT_DETAIL_SHOW_INLINE_GROUPS,
  });

  if (session == null) return null;

  const displaySummary = summary;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={PHYSICAL_COUNT_DETAIL_SHOW_INLINE_GROUPS ? 'lg' : 'md'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: theme.gradients.primaryCta,
          color: 'common.white',
          fontWeight: 600,
        }}
      >
        <span>Detalle: {session.code}</span>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'common.white' }}
          aria-label="Cerrar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: 'background.default' }}>
        {summaryLoading || displaySummary == null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {summaryError != null && (
              <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
                {summaryError}
              </Alert>
            )}
            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={600} color="text.primary" gutterBottom>
                Resumen
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Código
                  </Typography>
                  <Typography variant="body1" fontWeight={500} color="text.primary">
                    {displaySummary.code}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Sucursal
                  </Typography>
                  <Typography variant="body1" fontWeight={500} color="text.primary">
                    {displaySummary.locationName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Fecha
                  </Typography>
                  <Typography variant="body1" fontWeight={500} color="text.primary">
                    {formatDisplayDateTime(displaySummary.sessionDate)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Comparado en
                  </Typography>
                  <Typography variant="body1" fontWeight={500} color="text.primary">
                    {formatDisplayDateTime(displaySummary.comparedAt)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                {PHYSICAL_COUNT_METRICS.map((metric) => (
                  <SummaryStatCard
                    key={metric.key}
                    label={metric.detailLabel}
                    value={metric.getSessionValue(displaySummary)}
                    accent={DETAIL_METRIC_ACCENTS[metric.key]}
                    tooltip={metric.tooltip}
                  />
                ))}
              </Box>
            </Box>

            {PHYSICAL_COUNT_DETAIL_SHOW_INLINE_GROUPS && (
              <Box sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                    Detalle por categoría
                  </Typography>
                  {hasExpandableGroups && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={expandAllGroups}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                        }}
                      >
                        Expandir todos
                      </Button>
                      <Button
                        size="small"
                        onClick={collapseAllGroups}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                        }}
                      >
                        Colapsar todos
                      </Button>
                    </Box>
                  )}
                </Box>

                {PHYSICAL_COUNT_DETAIL_GROUPS.map((cfg) => {
                  const count = cfg.getCount(displaySummary);
                  return (
                    <PhysicalCountDetailGroup
                      key={cfg.key}
                      groupKey={cfg.key}
                      label={cfg.label}
                      count={count}
                      accent={cfg.accent}
                      helperText={cfg.helperText}
                      state={groups[cfg.key]}
                      disabled={count <= 0}
                      onToggle={() => toggleGroup(cfg.key)}
                      onPageChange={(page) => changeGroupPage(cfg.key, page)}
                      onItemsPerPageChange={(event) => changeGroupItemsPerPage(cfg.key, event)}
                    />
                  );
                })}
              </Box>
            )}

            <Box
              sx={{
                p: 3,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: (t) =>
                  t.palette.mode === 'dark'
                    ? alpha(t.palette.common.white, 0.04)
                    : t.palette.grey[50],
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                Auditoría
              </Typography>
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Realizado por
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {displaySummary.createdBy ?? '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Creado el
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {formatDisplayDateTime(displaySummary.createdAt)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          backgroundImage: 'none',
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={(t) => ({
            textTransform: 'none',
            borderColor: 'divider',
            color: 'text.secondary',
            backgroundColor: t.palette.background.paper,
            '&:hover': {
              backgroundColor: t.palette.action.hover,
              color: 'text.primary',
              borderColor: 'divider',
            },
          })}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhysicalCountDetailModal;
